import { Component, useEffect, useRef, useState } from 'react';
import { ecranTactile } from '../lib/ecran-tactile';
import { tr } from '../lib/i18n';
import { PagesPdf } from './PagesPdf.jsx';
import { C } from '../theme/palette';

/**
 * La visionneuse : un fichier (certificat médical, fiche de salaire, PDF…)
 * s'ouvre par-dessus SwissTech, avec un gros bouton « ← Retour ».
 *
 * <p>Dans l'application installée sur l'écran d'accueil, un lien « nouvel
 * onglet » ouvrait le fichier à la place de SwissTech, sans barre ni bouton :
 * le patron ne pouvait plus revenir en arrière. Le fichier reste maintenant
 * dans SwissTech ; « Retour », le geste retour du téléphone et la touche
 * Échap ferment la visionneuse.
 */

let ouvrir = null;

/**
 * Ouvre un fichier dans la visionneuse.
 *
 * @param {{adresse?: string, blob?: Blob, promesse?: Promise<{blob: Blob, titre?: string}>, titre?: string}} fichier
 *   l'adresse à charger (avec la session), le fichier déjà chargé, ou le
 *   fichier en cours de préparation (la visionneuse s'ouvre aussitôt et attend)
 * @returns {boolean} faux si la visionneuse n'est pas affichée : à l'appelant
 *   d'ouvrir le fichier autrement
 */
export function ouvreVisionneuse(fichier) {
  if (!ouvrir) return false;
  ouvrir(fichier);
  return true;
}

/**
 * Le genre d'un fichier : celui que le serveur annonce, sinon reconnu à ses
 * premiers octets (une photo ou un PDF déposé sans type).
 */
async function typeDe(blob) {
  const annonce = String(blob.type || '').toLowerCase();
  if (annonce.startsWith('image/') || annonce === 'application/pdf') return annonce;
  try {
    const debut = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
    const hex = Array.from(debut, (o) => o.toString(16).padStart(2, '0')).join('');
    if (hex === '25504446') return 'application/pdf';
    if (hex.startsWith('ffd8ff')) return 'image/jpeg';
    if (hex === '89504e47') return 'image/png';
  } catch {
    // Illisible : on garde le type annoncé.
  }
  return annonce;
}

/** Vrai quand la visionneuse attend un PDF que le serveur prépare. */
function promesseEnCours(o) {
  return !!o?.prepare;
}

/** Vrai pour un lien vers un fichier de l'API SwissTech qui s'ouvrirait dans un onglet. */
function estFichierApi(a) {
  if (!a || a.target !== '_blank' || a.hasAttribute('download')) return false;
  try {
    const url = new URL(a.href, window.location.href);
    return url.origin === window.location.origin && url.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

const barre = {
  display: 'flex',
  // Sur un iPhone, le titre serré entre les boutons se réduisait à « f… » :
  // trop étroit, il passe sur sa propre ligne.
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  padding: 'calc(var(--st-haut) + 10px) 12px 10px',
  background: C.navy,
  color: '#fff',
};
const boutonBarre = {
  border: 'none',
  borderRadius: 10,
  padding: '10px 14px',
  fontSize: 13,
  fontWeight: 800,
  cursor: 'pointer',
};

/**
 * Une erreur dans l'affichage d'un fichier reste dans la visionneuse : elle
 * ne doit jamais faire tomber SwissTech (écran blanc à recharger).
 */
class Garde extends Component {
  constructor(props) {
    super(props);
    this.state = { erreur: false };
  }

  static getDerivedStateFromError() {
    return { erreur: true };
  }

  render() {
    return this.state.erreur ? (
      <div style={{ color: '#fff', textAlign: 'center', padding: 40, fontSize: 13 }}>
        {tr('Le fichier n’a pas pu être ouvert.')}
      </div>
    ) : (
      this.props.children
    );
  }
}

/**
 * L'hôte de la visionneuse, monté une fois à la racine de l'application.
 *
 * @returns {import('react').ReactElement|null} la visionneuse ouverte, ou rien
 */
export function Visionneuse() {
  const [ouvert, setOuvert] = useState(null);
  const historique = useRef(false);
  // L'agrandissement : la page de l'application interdit le zoom du
  // navigateur (le patron ne pouvait pas agrandir le PDF). La visionneuse a le
  // sien : deux doigts, double toucher, ou les boutons − et +.
  const [zoom, setZoom] = useState(1);
  const zone = useRef(null);
  const zoomRef = useRef(1);
  zoomRef.current = zoom;
  const ZOOM_MAX = 4;
  const changeZoom = (voulu, centre) => {
    const el = zone.current;
    const avant = zoomRef.current;
    const apres = Math.min(ZOOM_MAX, Math.max(1, Math.round(voulu * 100) / 100));
    if (apres === avant) return;
    // Le point sous les doigts reste sous les doigts.
    const cx = centre ? centre.x : el ? el.clientWidth / 2 : 0;
    const cy = centre ? centre.y : el ? el.clientHeight / 2 : 0;
    const sx = el ? (el.scrollLeft + cx) / avant : 0;
    const sy = el ? (el.scrollTop + cy) / avant : 0;
    setZoom(apres);
    requestAnimationFrame(() => {
      if (!el) return;
      el.scrollLeft = sx * apres - cx;
      el.scrollTop = sy * apres - cy;
    });
  };
  useEffect(() => {
    const el = zone.current;
    if (!el) return undefined;
    let pince = null;
    let dernierToucher = 0;
    const ecart = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const milieu = (t) => {
      const r = el.getBoundingClientRect();
      return {
        x: (t[0].clientX + t[1].clientX) / 2 - r.left,
        y: (t[0].clientY + t[1].clientY) / 2 - r.top,
      };
    };
    const debut = (ev) => {
      if (ev.touches.length === 2) {
        pince = { ecart: ecart(ev.touches), zoom: zoomRef.current };
      } else if (ev.touches.length === 1) {
        const maintenant = Date.now();
        if (maintenant - dernierToucher < 300) {
          const r = el.getBoundingClientRect();
          const t = ev.touches[0];
          changeZoom(zoomRef.current > 1 ? 1 : 2.5, {
            x: t.clientX - r.left,
            y: t.clientY - r.top,
          });
          dernierToucher = 0;
        } else dernierToucher = maintenant;
      }
    };
    const bouge = (ev) => {
      if (!pince || ev.touches.length !== 2) return;
      ev.preventDefault();
      changeZoom((pince.zoom * ecart(ev.touches)) / pince.ecart, milieu(ev.touches));
    };
    const fin = (ev) => {
      if (ev.touches.length < 2) pince = null;
    };
    el.addEventListener('touchstart', debut, { passive: true });
    el.addEventListener('touchmove', bouge, { passive: false });
    el.addEventListener('touchend', fin, { passive: true });
    return () => {
      el.removeEventListener('touchstart', debut);
      el.removeEventListener('touchmove', bouge);
      el.removeEventListener('touchend', fin);
    };
    // La zone change à chaque fichier ouvert.
  }, [ouvert?.url]);

  const ferme = () => {
    setZoom(1);
    setOuvert((o) => {
      if (o?.url) setTimeout(() => URL.revokeObjectURL(o.url), 0);
      return null;
    });
  };

  // Retour : par l'historique quand la visionneuse y a laissé sa trace, pour
  // que le geste retour du téléphone et le bouton fassent la même chose.
  const retour = () => {
    if (historique.current) window.history.back();
    else ferme();
  };

  useEffect(() => {
    ouvrir = ({ adresse, blob, promesse, titre }) => {
      setOuvert({
        titre: titre || '',
        chargement: !blob,
        prepare: !!promesse,
        url: null,
        type: '',
        erreur: '',
      });
      if (!historique.current) {
        window.history.pushState({ visionneuse: true }, '');
        historique.current = true;
      }
      // Un PDF que le serveur prépare (offre, facture) : la visionneuse est
      // déjà ouverte, « Préparation… » à l'écran. Sans elle, le bouton
      // semblait ne rien faire tant que le serveur travaillait.
      const charge = blob
        ? Promise.resolve({ blob })
        : promesse
          ? promesse
          : fetch(adresse, { credentials: 'include' }).then((r) => {
              if (!r.ok) throw new Error(String(r.status));
              return r.blob().then((b) => ({ blob: b }));
            });
      charge
        .then(async ({ blob: b, titre: t }) => {
          const type = await typeDe(b);
          setOuvert((o) =>
            o
              ? {
                  ...o,
                  titre: o.titre || t || '',
                  chargement: false,
                  url: URL.createObjectURL(b),
                  type,
                  blob: b,
                }
              : o,
          );
        })
        .catch((e) =>
          setOuvert((o) =>
            o
              ? {
                  ...o,
                  chargement: false,
                  erreur:
                    promesse && e?.message ? e.message : tr('Le fichier n’a pas pu être ouvert.'),
                }
              : o,
          ),
        );
    };
    const auRetour = () => {
      historique.current = false;
      ferme();
    };
    // Les liens « nouvel onglet » vers un fichier de SwissTech s'ouvrent ici.
    const auClic = (ev) => {
      if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey) return;
      const a = ev.target.closest?.('a');
      if (!estFichierApi(a)) return;
      ev.preventDefault();
      ouvrir({ adresse: a.href, titre: (a.textContent || '').trim() });
    };
    window.addEventListener('popstate', auRetour);
    document.addEventListener('click', auClic);
    return () => {
      ouvrir = null;
      window.removeEventListener('popstate', auRetour);
      document.removeEventListener('click', auClic);
    };
  }, []);

  useEffect(() => {
    if (!ouvert) return undefined;
    const echap = (ev) => ev.key === 'Escape' && retour();
    window.addEventListener('keydown', echap);
    return () => window.removeEventListener('keydown', echap);
  });

  if (!ouvert) return null;

  const enregistre = async () => {
    const nom =
      (ouvert.titre || 'document').replace(/[^\p{L}\p{N} _.-]+/gu, '').trim() || 'document';
    const extension = ouvert.type.includes('pdf')
      ? '.pdf'
      : ouvert.type.startsWith('image/')
        ? '.' + ouvert.type.slice(6).replace('jpeg', 'jpg')
        : '';
    const fichier = new File([ouvert.blob], nom + extension, { type: ouvert.type });
    if (navigator.canShare?.({ files: [fichier] })) {
      try {
        await navigator.share({ files: [fichier], title: ouvert.titre });
        return;
      } catch {
        // Partage annulé : rien d'autre à faire.
        return;
      }
    }
    const lien = document.createElement('a');
    lien.href = ouvert.url;
    lien.download = fichier.name;
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
  };

  const peutPartager =
    !!ouvert.blob &&
    !!navigator.canShare?.({ files: [new File([ouvert.blob], 'document', { type: ouvert.type })] });

  // Imprimer : sur un téléphone, la feuille de partage propose « Imprimer » ;
  // à l'ordinateur, le fichier part à l'imprimante tel quel, toutes ses pages.
  const imprime = () => {
    // Le partage seulement sur un écran tactile : Chrome sous Windows sait
    // aussi partager un fichier, mais là on veut l'imprimante.
    const tactile = ecranTactile();
    const fichier = new File([ouvert.blob], 'document', { type: ouvert.type });
    if (tactile && navigator.canShare?.({ files: [fichier] })) {
      enregistre();
      return;
    }
    const cadre = document.createElement('iframe');
    cadre.title = tr('Imprimer');
    cadre.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
    cadre.onload = () => {
      try {
        cadre.contentWindow.focus();
        cadre.contentWindow.print();
      } catch {
        window.open(ouvert.url, '_blank', 'noopener');
      }
      setTimeout(() => cadre.remove(), 60000);
    };
    cadre.src = ouvert.url;
    document.body.appendChild(cadre);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ouvert.titre || tr('Document')}
      data-testid="visionneuse"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        background: '#1f2937',
      }}
    >
      <div style={barre}>
        <button
          type="button"
          onClick={retour}
          style={{ ...boutonBarre, background: '#fff', color: C.navy }}
        >
          {tr('← Retour')}
        </button>
        <div
          style={{
            flex: '1 1 140px',
            minWidth: 0,
            fontSize: 12,
            fontWeight: 700,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {ouvert.titre}
        </div>
        {ouvert.url && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              type="button"
              aria-label={tr('Réduire')}
              onClick={() => changeZoom(zoom / 1.5)}
              disabled={zoom <= 1}
              style={{
                ...boutonBarre,
                padding: '10px 12px',
                background: 'rgba(255,255,255,.15)',
                color: '#fff',
                opacity: zoom <= 1 ? 0.4 : 1,
              }}
            >
              −
            </button>
            <button
              type="button"
              aria-label={tr('Taille normale')}
              onClick={() => changeZoom(1)}
              style={{
                ...boutonBarre,
                padding: '10px 6px',
                background: 'transparent',
                color: '#fff',
                fontSize: 11,
                minWidth: 44,
              }}
            >
              {Math.round(zoom * 100)} %
            </button>
            <button
              type="button"
              aria-label={tr('Agrandir')}
              onClick={() => changeZoom(zoom * 1.5)}
              disabled={zoom >= ZOOM_MAX}
              style={{
                ...boutonBarre,
                padding: '10px 12px',
                background: 'rgba(255,255,255,.15)',
                color: '#fff',
                opacity: zoom >= ZOOM_MAX ? 0.4 : 1,
              }}
            >
              +
            </button>
          </div>
        )}
        {ouvert.url && (
          <button
            type="button"
            onClick={imprime}
            style={{ ...boutonBarre, background: 'rgba(255,255,255,.15)', color: '#fff' }}
          >
            {tr('🖨 Imprimer')}
          </button>
        )}
        {ouvert.url && (
          <button
            type="button"
            onClick={enregistre}
            style={{ ...boutonBarre, background: 'rgba(255,255,255,.15)', color: '#fff' }}
          >
            {/* Sur le téléphone, le même bouton ouvre le partage : e-mail,
                WhatsApp, n'importe quelle application. */}
            {peutPartager ? tr('📤 Envoyer') : tr('📤 Enregistrer')}
          </button>
        )}
      </div>
      <div
        ref={zone}
        data-testid="zone-visionneuse"
        style={{ flex: 1, minHeight: 0, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}
      >
        <div style={{ width: zoom * 100 + '%' }}>
          {ouvert.chargement && (
            <div style={{ color: '#fff', textAlign: 'center', padding: 40, fontSize: 13 }}>
              {promesseEnCours(ouvert) ? tr('⏳ Préparation du PDF…') : tr('⏳ Ouverture…')}
            </div>
          )}
          {ouvert.erreur && (
            <div style={{ color: '#fff', textAlign: 'center', padding: 40, fontSize: 13 }}>
              {ouvert.erreur}
            </div>
          )}
          {ouvert.url && ouvert.type.startsWith('image/') && (
            <img
              src={ouvert.url}
              alt={ouvert.titre}
              style={{
                display: 'block',
                width: zoom > 1 ? '100%' : 'auto',
                maxWidth: '100%',
                margin: '0 auto',
              }}
            />
          )}
          {ouvert.url && ouvert.type === 'application/pdf' && !ouvert.erreur && (
            <Garde>
              <PagesPdf
                blob={ouvert.blob}
                onErreur={() =>
                  setOuvert((o) =>
                    o ? { ...o, erreur: tr('Le fichier n’a pas pu être ouvert.') } : o,
                  )
                }
              />
            </Garde>
          )}
          {ouvert.url && !ouvert.type.startsWith('image/') && ouvert.type !== 'application/pdf' && (
            <div style={{ color: '#fff', textAlign: 'center', padding: 40, fontSize: 13 }}>
              {tr('Ce fichier ne s’affiche pas ici : touche « 📤 Enregistrer » pour l’ouvrir.')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
